import { Search, X } from "lucide-react";
import { useId, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { DirectoryMatch } from "../api/types";
import { useDebouncedValue } from "../lib/hooks";
import { Avatar } from "../ui/Avatar";
import { Badge } from "../ui/Badge";
import { toQuery } from "./api";

/** Find a colleague: people already known to the desk, and the Entra directory once enabled. */
export function DirectoryPicker({
  label,
  hint,
  placeholder = "Search by name or email",
  hideLabel = false,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  placeholder?: string;
  /** Where a heading above already says what it's for: the label is for screen readers only. */
  hideLabel?: boolean;
  value: DirectoryMatch | null;
  onChange: (person: DirectoryMatch | null) => void;
}) {
  const id = useId();
  const [search, setSearch] = useState("");
  const q = useDebouncedValue(search.trim(), 250);
  const results = useQuery({
    queryKey: ["directory", "lookup", q],
    queryFn: ({ signal }) =>
      api<DirectoryMatch[]>(`/api/directory/lookup?${toQuery({ q })}`, {
        signal,
      }),
    enabled: q.length >= 2 && value === null,
    placeholderData: keepPreviousData,
  });

  if (value) {
    return (
      <div className="field">
        <span className="field-label">{label}</span>
        <div className="picked-person">
          <Avatar name={value.name} seed={value.email} size="sm" />
          <div className="cell-person-text">
            <span className="cell-strong">{value.name}</span>
            <span className="cell-secondary">
              {value.email}
              {value.job_title ? `, ${value.job_title}` : ""}
              {value.department ? `, ${value.department}` : ""}
            </span>
          </div>
          <button
            type="button"
            className="icon-btn icon-btn-sm"
            aria-label={`Change ${label.toLowerCase()}`}
            onClick={() => onChange(null)}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field person-picker">
      <label className={hideLabel ? "visually-hidden" : "field-label"} htmlFor={id}>
        {label}
      </label>
      {hint ? <span className="field-hint">{hint}</span> : null}
      <div className="search">
        <Search className="search-icon" size={16} aria-hidden="true" />
        <input
          id={id}
          className="input"
          type="search"
          placeholder={placeholder}
          value={search}
          maxLength={100}
          autoComplete="off"
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {q.length >= 2 ? (
        results.data?.length ? (
          <ul className="picker-results" aria-label="Matching people">
            {results.data.map((person) => (
              <li key={person.email}>
                <button
                  type="button"
                  className="picker-result"
                  onClick={() => onChange(person)}
                >
                  <Avatar name={person.name} seed={person.email} size="sm" />
                  <span className="cell-person-text">
                    <span className="cell-strong">{person.name}</span>
                    <span className="cell-secondary">
                      {person.email}
                      {person.department ? `, ${person.department}` : ""}
                    </span>
                  </span>
                  {person.source === "directory" ? (
                    <Badge tone="primary">Directory</Badge>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : results.isFetching ? null : (
          <p className="muted picker-empty">
            No matches. Try an email address.
          </p>
        )
      ) : null}
    </div>
  );
}
